/**
 * bbLegacyFieldOwnership — flat brand_bible PAYLOAD column → owning Brand
 * Bible letter, for the explicit-save autosave-skip filter
 * (ai-docs/architect-briefs/bb-phase2-explicit-save-brief-20260810.md).
 *
 * Once a letter joins CONVERTED_SECTIONS (components/brand-bible/save), the
 * page's debounced autosave and saveDraftNow must stop persisting the flat
 * columns that letter owns — the section's own Save button becomes their only
 * write path (via the page's commitDraftFields seam, which re-includes the
 * saving letter's columns). Filtering at the payload-column level (not the
 * draft-field level) keeps composites atomic: brand_restrictions folds three
 * §G draft fields (words_avoid, services_not_offered, regulatory_notes) into
 * one column, and a column either ships whole or not at all.
 *
 * Ownership entries are added by each section's conversion PR alongside its
 * CONVERTED_SECTIONS letter — an entry here for an unconverted letter is
 * harmless (the filter only consults converted letters). Seeded with §G's
 * columns, the only ownership the design brief states outright.
 *
 * Deliberately ABSENT (do not add):
 * - credentials_licenses / case_studies / social_media_links (§D) — §D is
 *   CONVERTED, and still claims none of these on purpose. Its buffered
 *   editor writes bb_* rows only; the flat columns belong to page-level
 *   controls it does not own. Claiming them would strip the columns from the
 *   legacy PUT for NON-v2 tenants, whose textareas are their only editor and
 *   that PUT their only write path — a data-loss regression in exchange for
 *   nothing, since §D's Save never commits a flat field. (For v2-active
 *   tenants those columns already 422 on /legacy-fields and are hidden by
 *   PR #1623; social_media_links persists nowhere at all — a separate §D
 *   fast-follow, not something this map can fix.)
 * - theme_config — no longer sent at all (class `context`; its v2 write home
 *   is bb_visual_identity; sending it 422'd the whole legacy-fields batch).
 * - visual_identity (§H "Imagery & references") — §H IS converted (VisualIdentityPanel
 *   buffers + Saves, and commits this column through the useBbDraftCommit seam),
 *   but the column is NOT claimed here because BrandBiblePage still renders a
 *   SECOND, page-level imagery textarea for the same column (it also feeds the
 *   §H header AI-suggest pill). Claiming visual_identity for 'H' would make the
 *   autosave strip that column, orphaning the page-level textarea with no Save
 *   path behind it — the exact silent data loss this file warns about. Adding
 *   the entry waits on the architect's §H mount-swap that removes the page-level
 *   textarea (same two-step §A follows). Until then the panel's imagery commit
 *   through the seam is harmless (the column is unstripped, includeLetters is a
 *   no-op) and autosave continues to persist the page-level control.
 * - ideal_client / seasonality (§E) — class `context` (bb_chunks homes
 *   icp_note / seasonality_note); they never route through the flat path, so
 *   there is nothing for autosave to skip. Ruled in the §E save-lane prep:
 *   converting §E must route these to their chunks or hide the legacy
 *   controls (PR #1623 pattern) — never buffer them through this map.
 */
import type { BbLetter } from '@/lib/bbSectionMap';

export const BB_FLAT_COLUMN_OWNER: Readonly<Partial<Record<string, BbLetter>>> = {
  /** words_avoid + words_to_avoid + services_not_offered + regulatory_notes */
  brand_restrictions: 'G',
  /**
   * §F's flat `competitors` column is the never-link/blocklist MIRROR, NOT the
   * typed competitive set. §F's real payload is the typed competitor records in
   * bb_chunks (section='F', kind='competitor'), which never route through this
   * flat path. This column carries the page-level "Never-link list"
   * (tenant_blocklist / link policy `never_use`) domains into the legacy
   * payload. The blocklist's authoritative home is the link-policy table — the
   * page writes it immediately via useAddToPolicy/useRemoveFromPolicy and always
   * READS it back from that table (BrandBiblePage.fromBrandBible reads the
   * blocklist, never bb.competitors), so this flat column is a write-only
   * mirror. Marking it §F-owned stops the debounced page autosave from racing
   * that immediate write once §F is converted; the blocklist itself is never
   * dropped. NOTE: the v2 projection ALSO writes this column from bb_chunks — a
   * pre-existing dual-writer condition, NOT introduced by the §F conversion.
   */
  competitors: 'F',
  /**
   * §A (Essentials/Identity) owns every flat brand_bible column its buffered
   * draft scalars back. Once 'A' joins CONVERTED_SECTIONS the page autosave
   * must stop persisting these — SectionEssentials' Save button commits them
   * via commitDraftFields (includeLetters=['A'] re-admits them past this
   * filter). Unlike §D, §A DOES claim its columns: for a non-v2 tenant the
   * legacy PUT is still the write path, and §A's Save re-includes them there,
   * so nothing is stripped without a replacement writer.
   *  - business_name / description — top-level scalar columns.
   *  - contact_info — the {website, phone, email, address} object; §A is its
   *    sole writer, so it ships whole or not at all.
   *  - service_locations — the service-area array column.
   */
  business_name: 'A',
  description: 'A',
  contact_info: 'A',
  service_locations: 'A',
  // §E · Market & Customer — the three convertible legacy_only columns. Each
  // draft field maps 1:1 to a same-named payload column in buildBrandBiblePayload
  // (no composite fold), so the column names below equal the draft keys.
  // ideal_client / seasonality stay OUT — see "Deliberately ABSENT" above.
  customer_pain_points: 'E',
  website_audience: 'E',
  buyer_personas: 'E',

  /**
   * §I — Channel Rules deliberately contributes NOTHING here, and its
   * conversion PR (feat/bb-save-i) adds no entry.
   *
   * §I lives entirely in bb_chunks (kinds headline / cta_preference /
   * review_response_template / escalation_trigger). It writes no flat
   * brand_bible column, so the page's autosave has nothing of §I's to persist
   * and therefore nothing to skip once 'I' joins CONVERTED_SECTIONS.
   *
   * The tempting entry is `call_to_action`, and it would be wrong. That column
   * is class `legacy_only`, is populated with hand-entered client copy, and is
   * singular where §I holds many cta_preference rows. Claiming it here would
   * hand a live client's CTA text to a section that never writes it. Same
   * reasoning as the §E context fields listed above.
   */
};

/**
 * Remove payload columns owned by a converted section, except columns whose
 * owner is in `include` (the letter currently committing through its explicit
 * Save). Pure + non-mutating so both autosave call sites and tests share it.
 *
 * Generic over the payload type so callers keep their typed payload
 * (buildBrandBiblePayload returns the BrandBible shape): the return type
 * claims every key survives, which is true whenever no owned column is
 * stripped — stripped payloads only flow into the update PUT / legacy PATCH,
 * both of which treat absent columns as "don't touch".
 */
export function stripConvertedFlatColumns<T extends object>(
  payload: T,
  converted: ReadonlySet<BbLetter>,
  include?: readonly BbLetter[],
): T {
  const included = new Set(include ?? []);
  const out: Record<string, unknown> = {};
  for (const [column, value] of Object.entries(payload)) {
    const owner = BB_FLAT_COLUMN_OWNER[column];
    if (owner && converted.has(owner) && !included.has(owner)) continue;
    out[column] = value;
  }
  return out as T;
}
